import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  PCT_SLOT_WIDTH,
  HOURS_BUDGET_CARD_PX,
  HOURS_ACTUAL_CARD_PX,
  HOURS_ROW_GAP_PX,
  HOURS_CARD_MIN_HEIGHT_PX,
} from './pctLayout';

const formatHours = (value, maximumFractionDigits = 1) =>
  (value || 0).toLocaleString(undefined, { maximumFractionDigits });

const cardSx = {
  p: 1,
  border: '0.5px solid',
  borderColor: 'divider',
  borderRadius: 1,
  bgcolor: 'background.paper',
  boxSizing: 'border-box',
  minHeight: HOURS_CARD_MIN_HEIGHT_PX,
  display: 'flex',
  flexDirection: 'column',
};

const labelSx = {
  fontSize: '10px',
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  lineHeight: 1.2,
  minHeight: 14,
};

const valueSx = {
  fontSize: '18px',
  fontWeight: 700,
  lineHeight: 1.25,
  mt: 'auto',
  pt: 0.25,
};

/**
 * Budget and Actual stat cards (fixed px widths) + plain-text Actual % burned.
 * Widths match Shop/Field so rows align across columns.
 */
const HoursRow = ({ budgetHrs, actualHrs }) => {
  const actualPct =
    budgetHrs > 0 ? Math.min((actualHrs / budgetHrs) * 100, 999) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: `${HOURS_ROW_GAP_PX}px`,
        mb: 1.25,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: `${HOURS_ROW_GAP_PX}px`,
          flexShrink: 0,
        }}
      >
        <Box sx={{ ...cardSx, width: HOURS_BUDGET_CARD_PX }}>
          <Typography sx={labelSx}>Budget</Typography>
          <Typography sx={valueSx}>
            {formatHours(budgetHrs, 0)}
            <Box
              component="span"
              sx={{
                fontSize: '0.62em',
                color: 'text.secondary',
                fontWeight: 500,
                ml: 0.5,
              }}
            >
              hrs
            </Box>
          </Typography>
        </Box>
        <Box sx={{ ...cardSx, width: HOURS_ACTUAL_CARD_PX }}>
          <Typography sx={labelSx}>Actual</Typography>
          <Typography sx={valueSx}>
            {formatHours(actualHrs, 1)}
            <Box
              component="span"
              sx={{
                fontSize: '0.62em',
                color: 'text.secondary',
                fontWeight: 500,
                ml: 0.5,
              }}
            >
              hrs
            </Box>
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          width: PCT_SLOT_WIDTH,
          flexShrink: 0,
          minHeight: HOURS_CARD_MIN_HEIGHT_PX,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'flex-end',
          textAlign: 'right',
          pb: 0.125,
        }}
      >
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 700,
            lineHeight: 1.2,
            color: 'info.main',
          }}
        >
          {actualPct != null ? `${actualPct.toFixed(1)}%` : '—'}
        </Typography>
        {actualPct != null && (
          <Typography
            sx={{
              fontSize: '10px',
              color: 'text.secondary',
              fontWeight: 500,
              mt: 0.35,
              lineHeight: 1.2,
            }}
          >
            burned
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default HoursRow;
